<template>
  <div class="container research-data-div">
    <h1 class="research-data-title h1-research-plan">Commercial Vehicle Resources &amp; Data</h1>
    <div class="checkbox-selector-div">
      <fieldset class="selection-checkbox-form">
        <h3 class="selection-info-p">FleetREDI pulls commercial vehicle data from disparate sources and uses an innovative, hierarchical classification system to enable data integration and analysis at varying levels of aggregation. Below, see the data sources and tools integrated into FleetREDI analytics.</h3>
        <div class="selection-buttons-container">
          <button :class="['btn', 'btn-nav', { 'active': isActive === 'select'  }]" @click="handleClick('select')">Select by Category</button>
          <button :class="['btn', 'btn-nav', { 'active': isActive === 'search' }]" @click="handleClick('search')">Search by Keyword</button>
        </div>
        <div class="checkbox-select-show" v-show="isActive === 'select'">
        <div class="checkbox-heading">
          <h4 class="h4-filter">Filter by Category:</h4>
        </div>
        <div class="checkboxes-container">
          <div class="checkbox-column" v-for="category in categories" :key="category">
            <input type="checkbox" :value="category" v-model="selectedCategories" :id="category" aria-describedby="category-description">
            <label  class="check-box-label" :for="category">{{ categoryTypes[category] }}</label>
            </div>
          </div>
          <div class="results-div">
            <div class="accordion mb-2"  v-for="infoType in selectedInfoType" :key="infoType" id="filter-accordion">
              <div class="accordion-top-container">
                <div class="accordion-header" :class="`filter-accordion-header-${infoType}`" value="infoType">
                  <button class="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    :data-bs-target="`#filter-accordion-${infoType.id}`"
                    :aria-expanded="false"
                    :aria-controls="`filter-accordion-${infoType.id}`">
                    <div class="accordion-info">
                      <h3 class="accordion-span" for="infoType">{{ infoType.title }}</h3>
                      <p class="accordion-category"><b>Category: </b>{{infoType.category }}</p>
                    </div>
                  </button>
                </div>
              </div>
              <div :id="`filter-accordion-${infoType.id}`" class="accordion-collapse collapse" :class="`filter-accordion-collapse-${infoType}`" data-bs-parent="#filter-accordion">
                <div class="accordion-body">
                  <div class="row">
                    <div class="col-12 col-md-8">
                      <p class="info-about-p">{{ infoType.about }}</p>
                    </div>
                    <div class="col-12 col-md-4 text-center mt-3">
                      <a class="link-btn text-white" :href="infoType.url" target="_blank" rel="noopener noreferrer">Learn More</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </fieldset>
      <div v-if="selectedInfoType.length === 0" class="search-error">
        <p class="error-message"><em>There are no results for your search, please search again.</em></p>
      </div>
    </div>
    <div class="searchbar-show" v-show="isActive === 'search'">
      <h4>Search Resources:</h4>
      <div class="searchbar-div">
        <span class="search-icon"><font-awesome-icon :icon="['fas', 'search']" /></span>
        <input id="research-searchbar" class="searchbar-input" type="text" v-model="input" placeholder="Search by keyword..." >
      </div>
      <div class="accordion mb-2"  v-for="item in filteredList" :key="item" id="search-accordion">
      <div class="accordion-top-container">
        <div class="accordion-header" :class="`search-accordion-header-${item}`" :value="item">
          <button class="accordion-button"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="`#search-accordion-${item.id}`"
          :aria-expanded="false"
          :aria-controls="`search-accordion-${item.id}`">
            <div class="accordion-info">
              <h3 class="accordion-span" for="infoType">{{ item.title }}</h3>
              <p class="accordion-category"><b>Category: </b>{{item.category }}</p>
            </div>
          </button>
        </div>
      </div>
      <div :id="`search-accordion-${item.id}`" class="accordion-collapse collapse" :class="`search-accordion-collapse-${item}`" data-bs-parent="#search-accordion">
          <div class="accordion-body">
            <div class="row">
              <div class="col-12 col-md-8">
                <p class="info-about-p">{{ item.about }}</p>
              </div>
              <div class="col-12 col-md-4 text-center mt-3">
                <button class="link-btn"><a class="text-white" :href="item.url" target="_blank" rel="noopener noreferrer">Learn More</a></button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="filteredList.length === 0" class="search-error">
        <p class="error-message"><em>There are no results for your search, please search again.</em></p>
      </div>
    </div>
  </div>
  <FooterBar />
</template>

<script setup>
import { ref, computed } from 'vue';

const categoryTypes = ref({
  commercialVehiclePopulationData: 'Commercial Vehicle Population Data',
  commercialVehicleOperationData: 'Commercial Vehicle Operation Data',
  freightDemandMovementData: 'Freight Demand/Movement Data',
  freightRailData: 'Freight Rail Data',
  commercialVehicleMovementData: 'Commercial Vehicle/Movement Data',
  dutyCycleData: 'Duty Cycle Data',
  transitVehicleFleetData: 'Transit Vehicle Fleet Data',
  otherResources: 'Cross-Cutting Resources',
});
const researchData = {
  commercialVehiclePopulationData: [
    {
      id: 1,
      category: 'Commercial Vehicle Population Data',
      title: 'TipNet',
      all: true,
      about: 'The Trucking Industry Profile (TIPNet) provides information on light, medium, and heavy commercial trucks and new commercial trailers in North America. The tool allows users to segment vehicles by geography or market and gain insights into the trucks that make up regional markets.',
      url: 'https://www.spglobal.com/mobility/en/products/automotive-tipnet-commercial-vehicle-market-data.html',
    },
    {
      id: 2,
      category: 'Commercial Vehicle Population Data',
      title: 'Experian VIO',
      all: true,
      about: 'Experian VIO provides commercial-vehicle vocation detailing the use of light-duty, medium-duty and heavy-duty vehicles on the road today.',
      url: 'https://www.experian.com/automotive/vehicles-in-operation-vio-data',
    },
    {
      id: 3,
      category: 'Commercial Vehicle Population Data',
      title: 'NTEA Work Truck Upfits',
      all: true,
      about: 'The Association for the Work Truck Industry (NTEA) offers continually updated data and insights, including monthly market data updates, potential sales of work trucks and truck equipment by geographic market, and annual manufacturers’ shipments reports.',
      url: 'https://www.ntea.com/NTEA/Online_resources/NTEA/Member_benefits/Top_10_online_tools_for_members.aspx?hkey=3ca61727-83ea-4d8f-9e93-154696441fcf',
    },
  ],
  commercialVehicleOperationData: [
    {
      id: 4,
      category: 'Commercial Vehicle Operation Data',
      title: 'VIUS',
      all: true,
      about: 'The Vehicle Inventory and Use Survey (VIUS) is the principal data source on the physical and operational characteristics of the United States truck population, with data illustrating how trucks are used nationally for goods transportation.',
      url: 'https://www.bts.gov/vius',
    },
    {
      id: 5,
      category: 'Commercial Vehicle Operation Data',
      title: 'CA-VIUS',
      all: true,
      about: 'The California Vehicle Inventory and Use Survey (CA-VIUS) collected detailed characteristics of the truck population at a regional level for the entire state of California between 2016-2017.',
      url: 'https://dot.ca.gov/programs/transportation-planning/division-of-transportation-planning/data-analytics-services/statewide-modeling/california-vehicle-inventory-and-use-survey',
    },
    {
      id: 6,
      category: 'Commercial Vehicle Operation Data',
      title: 'VIUS Visualization Tool',
      all: true,
      about: 'The Federal Highway Administration’s (FHWA) VIUS Data Visualization tool allows users to rapidly create and download customized summaries from the Vehicle Inventory and Use Survey. VIUS includes information on the physical and operational characteristics of the U.S. truck population. This tool includes historical VIUS data for years 2002, 1997 and 1992 and will be updated to include the recently released VIUS2021 data.',
      url: 'https://explore.dot.gov/t/FHWA/views/VIUSVisualizationFinalv1_1/TruckRegistrationDashboard?%3Aembed=y&%3Aiid=4&%3AisGuestRedirectFromVizportal=y&%3Atabs=n',
    },
  ],
  freightDemandMovementData: [
    {
      id: 7,
      category: 'Freight Demand Movement Data',
      title: 'CFS',
      all: true,
      about: 'The Commodity Flow Survey (CFS), a component of the Economic Census, is a shipper survey that provides a comprehensive, multimodal picture of in-scope national freight flows and represents the only publicly available source of data for highway freight.',
      url: 'https://www.bts.gov/cfs',
    },
    {
      id: 8,
      category: 'Freight Demand Movement Data',
      title: 'FAF',
      all: true,
      about: 'The Freight Analysis Framework (FAF) creates a comprehensive picture of freight movement among states and major metropolitan areas by all modes of transportation. The FAF integrates data from the Commodity Flow Survey (CFS) and international trade data from the Census Bureau.',
      url: 'https://ops.fhwa.dot.gov/freight/freight_analysis/faf/',
    },
    {
      id: 9,
      category: 'Freight Demand Movement Data',
      title: 'FAF Visualization Tool',
      all: true,
      about: 'The Federal Highway Administration’s (FHWA) Data Visualization tool allows users to rapidly create and download customized freight summaries from the Freight Analysis Framework. FAF provides information about the amount and type of goods moved by various modes of transportation between large metropolitan areas, States, and regions for years 2017-2022, including forecasts for three different freight demand scenarios through 2050.',
      url: 'https://explore.dot.gov/t/FHWA/views/FAF5_5_1VisualizationFinalv1_1_09_14_2023/NationalSummaryDashboard?%3Aembed=y&%3Aiid=2&%3AisGuestRedirectFromVizportal=y&%3Atabs=n',
    },
    {
      id: 10,
      category: 'Freight Demand Movement Data',
      title: 'Quetica',
      all: true,
      about: 'Quetica offers an innovative approach to public sector freight planning combining best practices in freight transportation planning with an enhanced analytical approach.',
      url: 'https://quetica.com/',
    },
    {
      id: 11,
      category: 'Freight Demand Movement Data',
      title: 'Transearch',
      all: true,
      about: 'TranSearch data includes annual commodity flows between US counties by commodity type and mode of transportation, including truckload, less than-truckload, private truck, rail carload, rail/highway intermodal, air, and water.',
      url: 'https://www.spglobal.com/marketintelligence/en/mi/products/transearch-freight-transportation-research.html',
    },
    {
      id: 12,
      category: 'Freight Demand Movement Data',
      title: 'Truck Tonnage Report',
      all: true,
      about: 'ATA’s Monthly Truck Tonnage report provides insight into how freight tonnage is linked to broader economic trends. It includes an ATA generated index based on an ongoing survey of for-hire carriers that report monthly changes in tonnage hauled and explores the relevant economic indicators that can affect these volumes.',
      url: 'https://www.atabusinesssolutions.com/Shopping/Product/viewproduct/2066/ATA%20Monthly%20Truck%20Tonnage%20Report%20MTTR',
    },
  ],
  freightRailData: [
    {
      id: 13,
      category: 'Freight Rail Data',
      title: 'Analysis of Class I Railroads',
      all: true,
      about: 'The Association of American Railroads (AAR) Economic and Fiscal Impact Analysis of Class I Railroads calculates the economic and fiscal impacts of operations and capital investments of Class I railroads on the U.S. economy.',
      url: 'https://www.aar.org/wp-content/uploads/2018/11/AAR-Class-I-Railroad-Towson-Economic-Impact-October-2018.pdf',
    },
    {
      id: 14,
      category: 'Freight Rail Data',
      title: 'Freight Commodity Statistics',
      all: true,
      about: 'The AAR’s Freight Commodity Statistics report combines annual rail traffic data from the seven U.S. Class I railroads — BNSF, CN (GTC), CP (SOO), CSX, KCS, NS, and UP.',
      url: 'https://www.aar.org/data-center/rail-traffic-data/',
    },
    {
      id: 15,
      category: 'Freight Rail Data',
      title: 'Rail Time Indicators',
      all: true,
      about: 'The AAR’s Rail Time Indicators Report provides data on U.S. and Canadian rail traffic and details more than 15 key economic indicators, such as consumer confidence, housing starts, and industrial production.',
      url: 'https://www.aar.org/data-center/rail-traffic-data/',
    },
    {
      id: 16,
      category: 'Freight Rail Data',
      title: 'Railroad Facts',
      all: true,
      about: 'The AAR’s Railroad Facts publication provides financial and operating statistics for the U.S. Class I freight rail industry.',
      url: 'https://www.aar.org/publications/',
    },
    {
      id: 17,
      category: 'Freight Rail Data',
      title: 'Weekly Railroad Traffic',
      all: true,
      about: 'The AAR reports weekly U.S. rail traffic detailing total weekly rail traffic, rail volume, carloads, and more for the U.S., Canada, and Mexico.',
      url: 'https://www.aar.org/data-center/rail-traffic-data/',
    },
  ],
  commercialVehicleMovementData: [
    {
      id: 18,
      category: 'Commercial Vehicle/Movement Data',
      title: 'ATRI Truck GPS Database',
      all: true,
      about: 'The American Transportation Research Institute provides GPS-based spatial and temporal information for a large sample of trucks with onboard, wireless communication systems in the U.S.',
      url: 'https://icorridor-mto-on-ca.hub.arcgis.com/pages/atri',
    },
    {
      id: 19,
      category: 'Commercial Vehicle/Movement Data',
      title: 'INRIX',
      all: true,
      about: 'INRIX’s Trip Analytics provides accurate origin-destination data to understand transportation trends and the movement of vehicles, fleets, and freight.',
      url: 'https://inrix.com/products/trip-analytics/',
    },
    {
      id: 20,
      category: 'Commercial Vehicle/Movement Data',
      title: 'Geotab',
      all: true,
      about: 'Geotab provides rich data analysis from millions of vehicles across the globe. It leverages Google’s BigQuery to pull insights across billions of records. Then it categorizes and aggregates data for easy access and consumption.',
      url: 'https://data.geotab.com/',
    },
    {
      id: 21,
      category: 'Commercial Vehicle/Movement Data',
      title: 'Streetlight Data',
      all: true,
      about: 'StreetLight InSight consolidates and visualizes complex multi-mode traffic patterns, provides mobility metrics, and and allows users to ask their own mobility questions.',
      url: 'https://www.streetlightdata.com/',
    },
    {
      id: 22,
      category: 'Commercial Vehicle/Movement Data',
      title: 'TomTom',
      all: true,
      about: 'TomTom oversees the largest probe network in the automotive industry, combining information from more than 600 million connected devices and third-party sources. It provides information including real-time traffic, speed cameras, hazard warnings, fuel and weather services, and analytics.',
      url: 'https://www.tomtom.com/products/traffic-and-travel-information/',
    },
  ],
  dutyCycleData: [
    {
      id: 23,
      category: 'Duty Cycle Data',
      title: 'Fleet DNA',
      all: true,
      about: 'The Fleet DNA clearinghouse of commercial fleet vehicle operating data provides data summaries and visualizations similar to real-world "genetics" for medium- and heavy-duty fleet vehicles.',
      url: 'https://www.nlr.gov/transportation/fleettest-fleet-dna.html',
    },
  ],
  transitVehicleFleetData: [
    {
      id: 24,
      category: 'Transit Vehicle Fleet Data',
      title: 'APTA Factbook',
      all: true,
      about: 'The American Public Transportation Association Factbook, published annually, contains national aggregate statistical data covering all aspects of the transit industry in the United States.',
      url: 'https://www.apta.com/research-technical-resources/transit-statistics/public-transportation-fact-book/archive/',
    },
    {
      id: 25,
      category: 'Transit Vehicle Fleet Data',
      title: 'National Transit Database',
      all: true,
      about: 'The Federal Transit Administration’s National Transit Database records the financial, operating, and asset condition of transit systems. It provides information such as agency funding sources, inventories of vehicles and maintenance facilities, safety event reports, and measures of transit service provided and consumed.',
      url: 'https://www.transit.dot.gov/ntd',
    },
  ],
  otherResources: [
    {
      id: 26,
      category: 'Cross-Cutting Resources',
      title: 'BTS Transportation Data',
      all: true,
      about: 'Timely, high-quality statistics from the Bureau of Transportation Statistics provide national insight into transportation economics, travel, freight, system performance, and more.',
      url: 'https://data.bts.gov/stories/s/m9eb-yevh',
    },
    {
      id: 27,
      category: 'Cross-Cutting Resources',
      title: 'Service Annual Survey',
      all: true,
      about: 'The Service Annual Survey provides annual nationwide estimates of revenue, expenses, and other measures for most traditional service industries.',
      url: 'https://www.census.gov/programs-surveys/sas.html',
    },
    {
      id: 28,
      category: 'Cross-Cutting Resources',
      title: 'Quarterly Services Survey',
      all: true,
      about: 'The Quarterly Services Survey is the only source of service industry indicator performance providing timely estimates of revenue and expenses for selected service industries.',
      url: 'https://www.census.gov/services/historic_data.html',
    },
    {
      id: 29,
      category: 'Cross-Cutting Resources',
      title: 'FMCSA',
      all: true,
      about: 'The Federal Motor Carrier Safety Administration’s Registration Statistics data visualization tool allows users to build customized reports and drill down into data on registered large truck and bus companies and other industry partners.',
      url: 'https://ai.fmcsa.dot.gov/RegistrationStatistics/CustomReports.aspx',
    },
    {
      id: 30,
      category: 'Cross-Cutting Resources',
      title: 'Federal Highway Statistics',
      all: true,
      about: 'The Highway Statistics Series consists of annual reports containing analyzed statistical information on motor fuel, motor vehicle registrations, driver licenses, highway user taxation, highway mileage, travel, and highway finance.',
      url: 'https://www.fhwa.dot.gov/policyinformation/statistics.cfm',
    },
    {
      id: 31,
      category: 'Cross-Cutting Resources',
      title: 'Wards Intelligence',
      all: true,
      about: 'Wards Intelligence provides independent, unbiased global automotive research, intelligence and forecasting. Wards Intelligence’s Data Center allows users to run fully customizable reports for any time periods, at any level of granularity, and with multiple data sets in one result.',
      url: 'https://wardsintelligence.informa.com/datacenter',
    },
    {
      id: 32,
      category: 'Cross-Cutting Resources',
      title: 'GeoPlatform',
      all: true,
      about: 'The Geospatial Platform (GeoPlatform) is a strategic national resource that supports the Administration’s Open Government, Open Data and Digital Government strategies to enhance transparency, collaboration and participation.',
      url: 'https://www.geoplatform.gov/about',
    },
    {
      id: 33,
      category: 'Cross-Cutting Resources',
      title: 'Livewire',
      all: true,
      about: 'The Livewire Data Platform makes it easy to search and share transportation and mobility-related data. The Livewire Data Platform supports the U.S. Department of Energy’s Office of Critical Minerals and Energy Innovation’s Energy Efficient Mobility Systems (EEMS) Program goal of providing an affordable, efficient, safe, and accessible transportation future where mobility is decoupled from energy consumption.',
      url: 'https://livewire.energy.gov/',
    },
    {
      id: 34,
      category: 'Cross-Cutting Resources',
      title: 'Transportation Energy Data Book',
      all: true,
      about: 'Produced by Oak Ridge National Laboratory for the U.S. Department of Energy’s Office of Energy Vehicle Technologies Office, the Transportation Energy Data Book is a compendium of data on transportation with an emphasis on energy.',
      url: 'https://tedb.ornl.gov/',
    },
  ],
};
const isActive = ref('select');
const topics = Object.keys(researchData);
const selectedCategories = ref(topics);
const categories = Object.keys(researchData);
const input = ref('');

const handleClick = (type) => {
  isActive.value = type;
};

const selectedInfoType = computed(() => {
  const infoTypeArr = [];
  categories.forEach((category) => {
    if (selectedCategories.value.includes(category)) {
      infoTypeArr.push(...researchData[category]);
    }
  });
  return infoTypeArr;
});

const filteredList = computed(() => {
  const results = [];
  Object.keys(researchData).forEach((item) => {
    researchData[item].forEach((keywordObj) => {
      if (keywordObj.about.toLowerCase().includes(input.value.toLowerCase())) {
        results.push(keywordObj);
      }
    });
  });
  return results;
});
</script>
